/**
 * Rate limiting utilities for API calls
 */

// Exponential backoff with jitter
export const retryWithBackoff = async (apiCall, maxRetries = 3, baseDelay = 1000) => {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await apiCall()
    } catch (error) {
      if (error.response?.status === 429 && attempt < maxRetries - 1) {
        // Add jitter to prevent thundering herd
        const jitter = Math.random() * 0.1 * baseDelay
        const delay = baseDelay * Math.pow(2, attempt) + jitter
        console.log(`🔄 Rate limited, retrying in ${Math.round(delay)}ms (attempt ${attempt + 1}/${maxRetries})`)
        await new Promise(resolve => setTimeout(resolve, delay))
        continue
      }
      throw error
    }
  }
}

// Debounce utility
const debounceTimers = {}
export const debounce = (key, func, delay) => {
  if (debounceTimers[key]) {
    clearTimeout(debounceTimers[key])
  }
  return new Promise((resolve) => {
    debounceTimers[key] = setTimeout(() => {
      resolve(func())
    }, delay)
  })
}

// Throttle utility
const throttleTimers = {}
export const throttle = (key, func, delay) => {
  if (throttleTimers[key]) {
    return Promise.resolve(throttleTimers[key])
  }
  
  throttleTimers[key] = func().finally(() => {
    setTimeout(() => {
      delete throttleTimers[key]
    }, delay)
  })
  
  return throttleTimers[key]
}

// Request queue for sequential API calls
class RequestQueue {
  constructor() {
    this.queue = []
    this.processing = false
  }

  async add(request) {
    return new Promise((resolve, reject) => {
      this.queue.push({ request, resolve, reject })
      this.process()
    })
  }

  async process() {
    if (this.processing || this.queue.length === 0) return
    
    this.processing = true
    const { request, resolve, reject } = this.queue.shift()
    
    try {
      const result = await request()
      resolve(result)
    } catch (error) {
      reject(error)
    } finally {
      this.processing = false
      // Process next request after a small delay
      setTimeout(() => this.process(), 100)
    }
  }

  clear() {
    this.queue = []
    this.processing = false
  }
}

export const requestQueue = new RequestQueue()

// Rate limit detector
export const isRateLimited = (error) => {
  return error.response?.status === 429
}

// Get retry delay from response headers
export const getRetryDelay = (error) => {
  const retryAfter = error.response?.headers?.['retry-after']
  if (retryAfter) {
    return parseInt(retryAfter) * 1000
  }
  return 1000 // Default 1 second
}
